import { Fragment } from 'react'

export interface CountMatrixPlayer {
  id: number
  name: string
}

export interface CountMatrixCell {
  count: number
  won: number
}

interface CountMatrixProps {
  rows: CountMatrixPlayer[]
  cols: CountMatrixPlayer[]
  cellFor: (rowId: number, colId: number) => CountMatrixCell | null
  ariaLabel: string
  emptyMark?: string
}

// Fulde klasse-strenge (ikke sammensat af variabler), så Tailwind kan finde dem ved scanning.
const MINT_STEPS = ['bg-mint/10', 'bg-mint/18', 'bg-mint/30', 'bg-mint/45']

function stepIndex(ratio: number): number {
  if (ratio >= 0.75) return 3
  if (ratio >= 0.4) return 2
  if (ratio >= 0.15) return 1
  return 0
}

function cellBgClass(count: number, max: number): string {
  if (count === 0) return 'bg-text-2/10'
  const ratio = max > 0 ? count / max : 0
  return MINT_STEPS[stepIndex(ratio)]
}

/** Retningsbestemt matrix af antal pr. række×kolonne-par. Diagonalen er en gyldig celle (fx "selv"). */
function CountMatrix({ rows, cols, cellFor, ariaLabel, emptyMark = '·' }: CountMatrixProps) {
  let max = 0
  for (const row of rows) {
    for (const col of cols) {
      const cell = cellFor(row.id, col.id)
      if (cell && cell.count > max) max = cell.count
    }
  }

  return (
    <div
      className="grid gap-1 overflow-x-auto text-center text-[0.7rem]"
      style={{ gridTemplateColumns: `52px repeat(${cols.length}, minmax(44px, 1fr))` }}
      role="img"
      aria-label={ariaLabel}
    >
      <span />
      {cols.map((c) => (
        <span key={c.id} className="truncate px-0.5 py-1 text-text-2">
          {c.name}
        </span>
      ))}
      {rows.map((row) => (
        <Fragment key={row.id}>
          <span className="truncate py-1 text-left text-text-2">{row.name}</span>
          {cols.map((col) => {
            const cell = cellFor(row.id, col.id)
            const isSelf = row.id === col.id
            if (!cell || cell.count === 0) {
              return (
                <span key={`${row.id}-${col.id}`} className="rounded-md bg-text-2/5 py-2 font-mono text-text-2/40">
                  {emptyMark}
                </span>
              )
            }
            const title = isSelf
              ? `${row.name} · Selv: ${cell.count} gange, vundet ${cell.won}`
              : `${row.name} → ${col.name}: ${cell.count} gange, vundet ${cell.won}`
            return (
              <span
                key={`${row.id}-${col.id}`}
                title={title}
                className={`rounded-md py-1.5 font-mono font-semibold ${cellBgClass(cell.count, max)}`}
              >
                {cell.count}
              </span>
            )
          })}
        </Fragment>
      ))}
    </div>
  )
}

export default CountMatrix
